import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {ApiResponse, Attachment, RelatedToPayload} from '@core/types';
import {ENDPOINTS, GET_URL, log} from '@core/utils';
import {HttpService} from './http.service';
import {CompanyStateService} from '@core/states';
import {AuthStateService} from '@auth/states';

@Injectable({
  providedIn: 'root'
})
export class AttachmentService {
  constructor(
    private apiService: HttpService,
    private companyState: CompanyStateService,
    private authState: AuthStateService
  ) {}

  async uploadAttachments(params: RelatedToPayload & {
    companyId: string;
    files: File[];
    isAudio?: boolean;
    token?: string | null;
    skipLoader?: boolean;
  }): Promise<ApiResponse<Attachment[]>> {
    try {
      const url = GET_URL(ENDPOINTS.ATTACHMENTS);
      const formData = this.buildFormData(params.files, params.relatedToType, params.relatedToId, params.isAudio);
      const headers = this.buildHeaders(params.skipLoader);
      const response: any = await this.apiService.postForm(url, formData, {token: params.token, headers: headers || undefined, skipAuthLogout: true});
      const entries = Array.isArray(response) ? response : [response];
      return {success: true, result: entries.filter(Boolean).map((entry: any) => this.mapAttachment(entry))};
    } catch (error: any) {
      log.error('Error uploading attachments:', error);
    }
    return {success: false, message: 'No se pudieron subir los adjuntos.'}
  }

  async downloadAttachment(attachmentId: string | number, token?: string | null, options?: {skipLoader?: boolean}): Promise<ApiResponse<Blob>> {
    try {
      const url = GET_URL(ENDPOINTS.ATTACHMENT_DOWNLOAD, {id: attachmentId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: Blob = await this.apiService.get(url, {
        token,
        headers: headers || undefined,
        responseType: 'blob',
        skipAuthLogout: true
      });
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error downloading attachment:', error);
    }
    return {success: false, message: 'No se pudo descargar el adjunto.'};
  }

  async deleteAttachment(attachmentId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.ATTACHMENT_DETAIL, {id: attachmentId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deleting attachment:', error);
    }
    return {success: false, message: 'No se pudo eliminar el adjunto.'};
  }

  private mapAttachment(entry: any): Attachment {
    return {
      '@context': entry?.['@context'] || '',
      '@id': entry?.['@id'] || '',
      '@type': entry?.['@type'] || '',
      id: entry?.id,
      fileName: entry?.fileName || entry?.name || entry?.filename || 'Documento',
      fileSize: entry?.fileSize || entry?.size || entry?.bytes,
      mimeType: entry?.mimeType || entry?.type,
      filePath: entry?.filePath || entry?.path,
      createdAt: entry?.createdAt || entry?.created_at,
      uploadedById: entry?.uploadedById,
      uploadedByType: entry?.uploadedByType
    };
  }

  private buildFormData(files: File[], relatedToType: string, relatedToId: string | number, isAudio?: boolean) {
    const formData = new FormData();
    files.forEach(file => formData.append('files[]', file, file.name));
    formData.append('relatedToType', relatedToType);
    formData.append('relatedToId', String(relatedToId));
    if (typeof isAudio === 'boolean') {
      formData.append('isAudio', String(isAudio));
    }
    return formData;
  }

  private buildHeaders(skipLoader?: boolean): HttpHeaders | null {
    if (this.authState.isAdmin()) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    let headers = new HttpHeaders().set('X-Selected-Company-ID', String(companyId));
    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', '1');
    }
    return headers;
  }
}
